// Periodic / routine tasks — schedule templates that auto-spawn maintenance
// jobs at a fixed interval (days or engine hours). Extracted from
// Maintenance.jsx (P3.c). Contains the RoutineTab list, the read-only
// RoutineDetailModal, the RoutineEditor form and the per-yacht engine-unit
// helper functions they share.

import { useState, useEffect } from "react";
import { api, fmtErr } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../../components/ui/dialog";
import { Plus, Trash2, Wrench, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ServicePlanDialog } from "./ServicePlanDialog";
import { useAuth } from "../../../context/AuthContext";
import { useRights } from "../../../lib/useRights";
import { useYacht } from "../../../context/YachtContext";
import { useTour } from "../../../components/tour/TourContext";
import TourReplayButton from "../../../components/tour/TourReplayButton";
import { resolveUnitLocation } from "../../../lib/engineLocations";
import { Field } from "../Inventory";
import { StatusBadge } from "../Dashboard";
import { SystemSelect, PartsPicker, PriorityPill, Row } from "./components";
import { ExternalSupplierPicker } from "./ExternalSupplierPicker";
import { FindSupplierPicker } from "./FindSupplierPicker";
import { ROUTINE_DEFAULT, ENGINE_LIKE_SYSTEMS, PRIORITY, STATUS_GROUP_ORDER } from "./constants";

// Human cycle label for a routine — handles "whichever-first" tasks that carry
// both an hours and a days interval (e.g. "every 250h or 365 days").
function routineCycleLabel(r) {
  const days = r.recurrence_kind === "days" ? r.recurrence_value
    : (r.recurrence_kind === "engine_hours" && r.secondary_value ? r.secondary_value : null);
  const hours = r.recurrence_kind === "engine_hours" ? r.recurrence_value
    : (r.recurrence_kind === "days" && r.secondary_value ? r.secondary_value : null);
  const parts = [];
  if (hours) parts.push(`every ${hours}h`);
  if (days) parts.push(`every ${days} days`);
  return parts.join(" or ") || "—";
}

export function RoutineTab({ activeYacht, members, inventory, suppliers, can, onJobsChanged, spawnedJobs = [], onOpenJob }) {
  const { user } = useAuth();
  const { role: meRole } = useRights();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);  // routine pending delete-confirm
  const [detailRoutine, setDetailRoutine] = useState(null);  // routine open in the read-only detail modal
  const [seeding, setSeeding] = useState(false);
  const [servicePlanOpen, setServicePlanOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/routine-maintenance?yacht_id=${activeYacht.id}`);
      setRows(data);
    } catch (e) { toast.error(fmtErr(e)); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeYacht?.id]);

  const seedDefaults = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const { data } = await api.post(`/routine-maintenance/seed-defaults`, { yacht_id: activeYacht.id });
      const n = (data.seeded || []).length;
      if (n > 0) toast.success(`Seeded ${n} task template${n === 1 ? "" : "s"}. Open each to set the engine, interval and assignee.`);
      else toast("All task templates already exist.");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSeeding(false); }
  };

  const spawn = async (r) => {
    try {
      const { data } = await api.post(`/routine-maintenance/${r.id}/spawn`);
      if (data?.skipped) toast(`An open job from "${r.title}" already exists.`);
      else toast.success(`Maintenance job created from "${r.title}".`);
      load();
      onJobsChanged?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  // Two groups on the page:
  //  • "Tasks created" — manually created OR edited tasks (seeded flag cleared
  //     on edit). Sorted by due date (next_due; hours-only with no date last).
  //  • "Task Templates" — untouched seeded defaults. Sorted by system.
  const createdRoutines = rows
    .filter((r) => r.seeded !== true)
    .sort((a, b) => (a.next_due || "9999").localeCompare(b.next_due || "9999"));
  const templateRoutines = rows
    .filter((r) => r.seeded === true)
    .sort((a, b) => (a.system_category || "").localeCompare(b.system_category || "") || (a.title || "").localeCompare(b.title || ""));
  const confirmDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/routine-maintenance/${confirmDel.id}`);
      toast.success(`Routine "${confirmDel.title}" deleted.`);
      setConfirmDel(null);
      setDetailRoutine(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="mt-6" data-testid="routine-tab">
      <div className="flex flex-col gap-3 mb-3">
        <div className="text-[12px] text-zinc-600" data-testid="routine-tab-description">
          Recurring tasks auto-create in the <strong>Tasks triggered</strong> section below when due. Mark them done from there.
        </div>
        {can("routine:edit") && (
          <div className="flex flex-wrap gap-2 items-center">
            <Button variant="outline" onClick={seedDefaults} disabled={seeding} data-testid="seed-routines-btn">
              {seeding ? "Seeding…" : "Seed templates"}
            </Button>
            <Button variant="outline" onClick={() => setServicePlanOpen(true)} data-testid="service-plan-btn">
              <Wrench className="h-4 w-4 mr-1.5" />Suggest service plan
            </Button>
            <TourReplayButton tourId="periodic-task" />
            <Button className="bg-ink hover:bg-black" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-routine-btn">
              <Plus className="h-4 w-4 mr-2" />Periodic task
            </Button>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-md border border-zinc-200 bg-white p-8 text-center">
          <Wrench className="h-6 w-6 mx-auto text-zinc-300" />
          <div className="mt-2 text-sm text-zinc-700">No periodic tasks yet.</div>
          <div className="mt-1 text-[12px] text-zinc-500">
            e.g. <em>Engine oil change · every 90 days</em> or <em>Generator service · every 250h</em>.
          </div>
          {can("routine:edit") && (
            <Button
              variant="outline"
              className="mt-4"
              onClick={seedDefaults}
              disabled={seeding}
              data-testid="seed-routines-empty-btn"
            >
              {seeding ? "Seeding…" : "Seed task templates"}
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {createdRoutines.length > 0 && (
            <div data-testid="routine-section-created">
              <h3 className="font-display text-lg mb-2">
                Tasks created <span className="text-zinc-400 text-sm">({createdRoutines.length})</span>
              </h3>
              <RoutineList rows={createdRoutines} members={members} onOpen={setDetailRoutine} variant="created" />
            </div>
          )}
          {templateRoutines.length > 0 && (
            <div data-testid="routine-section-templates">
              <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                <h3 className="font-display text-lg">
                  Task Templates <span className="text-zinc-400 text-sm">({templateRoutines.length})</span>
                </h3>
                <span className="text-[11px] text-zinc-500">seeded templates — open one to configure it</span>
              </div>
              <RoutineList rows={templateRoutines} members={members} onOpen={setDetailRoutine} variant="templates" />
            </div>
          )}
        </div>
      )}

      <TriggeredJobs jobs={spawnedJobs} onOpenJob={onOpenJob} />

      {open && (
        <RoutineEditor
          template={editing}
          yacht_id={activeYacht.id}
          members={members}
          inventory={inventory}
          suppliers={suppliers}
          used={rows.map((r) => r.system_category)}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={() => { setOpen(false); setEditing(null); load(); }}
        />
      )}

      {detailRoutine && (
        <RoutineDetailModal
          routine={detailRoutine}
          members={members}
          can={can}
          userId={user?.id}
          userRole={meRole}
          onClose={() => setDetailRoutine(null)}
          onTrigger={async () => { await spawn(detailRoutine); }}
          onEdit={() => { const r = detailRoutine; setDetailRoutine(null); setEditing(r); setOpen(true); }}
          onDeleteRequest={() => setConfirmDel(detailRoutine)}
        />
      )}

      {confirmDel && (
        <Dialog open onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
          <DialogContent className="max-w-md" data-testid="delete-routine-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Delete routine schedule?
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-zinc-700">
              You're about to delete the routine{" "}
              <span className="font-semibold">"{confirmDel.title}"</span>.
            </div>
            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-800">
              No future maintenance jobs will be generated from this schedule.
              Any maintenance jobs already created from it stay in the Jobs tab
              and are not affected.
            </div>
            <DialogFooter className="mt-2">
              <Button variant="outline" onClick={() => setConfirmDel(null)} data-testid="delete-routine-cancel">
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDelete}
                data-testid="delete-routine-confirm"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete routine
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <ServicePlanDialog
        yachtId={activeYacht?.id}
        existingTitles={rows.map((r) => r.title || "")}
        open={servicePlanOpen}
        onClose={() => setServicePlanOpen(false)}
        onCreated={load}
      />
    </div>
  );
}

/* ─── Routine list (mobile cards + desktop table) ────────────────────
 * Renders a subset of routine templates. Used twice on the Periodic-tasks
 * page: once for "Tasks created" (configured tasks, by due date) and once for
 * "Task Templates" (untouched seeded defaults, by system).
 */
function RoutineList({ rows, members, onOpen, variant }) {
  return (
    <div className="space-y-2" data-testid={`routine-cards-${variant}`}>
      {rows.map((r) => {
        const assignee = members.find((m) => (m.user_id || m.id) === r.assigned_to);
        const cycle = routineCycleLabel(r);
        return (
          <div
            key={r.id}
            className={`rounded-lg border border-zinc-200 bg-white p-3 cursor-pointer transition-colors hover:border-zinc-400 hover:bg-zinc-50${r.deleted_at ? " opacity-60 [&_*]:line-through" : ""}`}
            data-testid={`routine-card-${r.id}`}
            onClick={() => onOpen(r)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(r); } }}
          >
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[11px] text-zinc-500 truncate">{r.system_category}{cycle ? ` · ${cycle}` : ""}</div>
                <div className="font-medium text-sm break-words">{r.title}</div>
                <div className="mt-0.5 text-[11px] text-zinc-500 truncate">
                  {assignee ? `Assigned: ${assignee.name || assignee.email}`
                    : (r.quote_suppliers || []).length ? `Quotes: ${r.quote_suppliers.map((s) => s.name).join(", ")}`
                    : r.external_supplier?.supplier_name ? `External: ${r.external_supplier.supplier_name} · offline`
                    : "Unassigned"}
                </div>
              </div>
              <div className="shrink-0 text-right">
                {r.recurrence_kind === "engine_hours" ? (
                  <div className={`text-sm font-semibold ${r.overdue ? "text-red-700" : ""}`}>
                    {r.next_due_hours ?? "—"} h
                    {r.current_hours != null && (
                      <div className="text-[10px] font-normal text-zinc-500">now {Math.round(r.current_hours)} h{r.engine_target_kind === "group" && r.engine_group ? ` · ${r.engine_group}` : ""}</div>
                    )}
                  </div>
                ) : (
                  <div className={`text-sm font-semibold ${r.overdue ? "text-red-700" : ""}`}>
                    {(r.next_due || "").slice(0, 10) || "—"}
                    <div className="text-[10px] font-normal text-zinc-500">next due</div>
                  </div>
                )}
                {r.overdue && (
                  <span className="mt-1 inline-flex text-[10px] uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 font-medium">due</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Triggered jobs ──────────────────────────────────────────────────
 * Maintenance jobs spawned from periodic tasks (manually via "Trigger now"
 * or by the auto-scheduler). They no longer appear on the Tasks page — they
 * live here, grouped by status, and open the shared JobDetailModal for full
 * status / cost / photo management (via onOpenJob).
 */
function TriggeredJobs({ jobs = [], onOpenJob }) {
  if (!jobs.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (j) =>
    !!j.due_date &&
    j.due_date.slice(0, 10) < today &&
    !["completed", "cancelled", "done"].includes(j.status);
  // Merge internal-terminal `done` into the `completed` bucket so users see a
  // single "Completed" group, matching the Tasks page.
  const bucketOf = (s) => (s === "done" ? "completed" : s);
  const groups = STATUS_GROUP_ORDER
    .filter((s) => s !== "done")
    .map((s) => ({
      status: s,
      // Sort each status group by due date (earliest first; no date last).
      items: jobs
        .filter((j) => bucketOf(j.status) === s)
        .sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999")),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="mt-10" data-testid="triggered-jobs">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-display text-lg">Tasks triggered</h3>
        <span className="text-zinc-400 text-sm">({jobs.length})</span>
      </div>
      <div className="text-[12px] text-zinc-500 mb-3">
        Jobs created from your periodic tasks — manually or by the scheduler. Click one to update status, cost and photos.
      </div>
      <div className="space-y-5">
        {groups.map((g) => (
          <div key={g.status} data-testid={`triggered-group-${g.status}`}>
            <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-zinc-200">
              <StatusBadge value={g.status} />
              <span className="text-[11px] text-zinc-500">{g.items.length}</span>
            </div>

            {/* Invoice-style card list — one layout for all screen sizes */}
            <div className="space-y-2">
              {g.items.map((j) => (
                <div
                  key={j.id}
                  data-testid={`triggered-card-${j.id}`}
                  onClick={() => onOpenJob?.(j)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenJob?.(j); } }}
                  className="rounded-lg border border-zinc-200 bg-white p-3 cursor-pointer transition-colors hover:border-zinc-400 hover:bg-zinc-50"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      {j.system_category && <div className="text-[11px] text-zinc-500 truncate">{j.system_category}</div>}
                      <div className="font-medium text-sm break-words">{j.title}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-500">
                        <PriorityPill v={j.priority} />
                        <span>
                          due{" "}
                          <span className={isOverdue(j) ? "text-red-700 font-medium" : ""}>
                            {(j.due_date || "").slice(0, 10) || "—"}
                          </span>
                        </span>
                        {isOverdue(j) && (
                          <span className="text-[10px] uppercase tracking-wider text-red-700 font-medium" data-testid={`triggered-overdue-${j.id}`}>overdue</span>
                        )}
                      </div>
                    </div>
                    {j.cost != null && (
                      <div className="shrink-0 text-right text-sm font-semibold font-mono">{j.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Routine detail modal ────────────────────────────────────────────
 * Read-only summary of a periodic-task template. Surfaces the three
 * actions the user can take on it (Trigger now, Edit, Delete) inline.
 * Trigger is the same `/routine-maintenance/{id}/spawn` call that the
 * scheduler runs every 30 min — useful when the user wants a job
 * generated ad-hoc (e.g. service truck just arrived, kick off the
 * checklist now).
 */
function RoutineDetailModal({ routine, members, can, userId, userRole, onClose, onTrigger, onEdit, onDeleteRequest }) {
  const r = routine;
  const assignee = members.find((m) => (m.user_id || m.id) === r.assigned_to);
  const cycle = routineCycleLabel(r);
  const [triggering, setTriggering] = useState(false);

  // Soft-deleted templates are read-only — Edit / Delete / Trigger all hide.
  const isDeleted = !!r.deleted_at;
  // Edit + Delete permission mirrors the backend rank rule. See
  // ``_can_edit_maint_record`` in routers/maintenance.py.
  const ROLE_RANK = { owner: 100, manager: 75, captain: 50, engineer: 20, accountant: 20, deckhand: 10, provider: 10 };
  const isCreator = r.created_by && r.created_by === userId;
  const creatorMember = members.find((m) => (m.user_id || m.id) === r.created_by);
  const creatorRole = creatorMember?.role || "deckhand";
  const outranksCreator = (ROLE_RANK[userRole] || 0) > (ROLE_RANK[creatorRole] || 0);
  const canEditThis = !isDeleted && (isCreator || (can("routine:edit") && outranksCreator));
  const canDeleteThis = !isDeleted && (isCreator || (can("routine:edit") && outranksCreator));

  const trigger = async () => {
    if (triggering) return;
    setTriggering(true);
    try { await onTrigger(); }
    finally { setTriggering(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto p-4 sm:p-6"
        data-testid="routine-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg pr-6 break-words">{r.title}</DialogTitle>
          <DialogDescription className="text-[12px] sm:text-sm">
            Periodic task — auto-creates a maintenance job in <strong>Tasks</strong> when it falls due.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mt-2">
          <Row label="Category" value={r.system_category} />
          <Row label="Priority" value={<PriorityPill v={r.priority} />} />
          <Row label="Cycle" value={cycle} />
          <Row
            label="Next due"
            value={
              r.recurrence_kind === "engine_hours" ? (
                <span className={r.overdue ? "text-red-700 font-medium" : ""}>
                  {r.next_due_hours ?? "—"} h
                  {r.current_hours != null && (
                    <span className="text-[11px] text-zinc-500 ml-1">· now {Math.round(r.current_hours)} h</span>
                  )}
                </span>
              ) : (
                <span className={r.overdue ? "text-red-700 font-medium" : ""}>
                  {(r.next_due || "").slice(0, 10) || "—"}
                </span>
              )
            }
          />
          <Row
            label="Assigned to"
            value={
              assignee ? `${assignee.name || assignee.email} · ${assignee.role}` :
              (r.quote_suppliers || []).length ? (
                <span data-testid="routine-detail-quote-suppliers">
                  {r.quote_suppliers.map((s) => s.name).join(", ")}
                  <span className="text-zinc-400 text-xs ml-1">· quote requests on each job</span>
                </span>
              ) :
              r.external_supplier?.supplier_name ? (
                <span data-testid="routine-detail-external-supplier">
                  {r.external_supplier.supplier_name}
                  <span className="text-zinc-400 text-xs ml-1">· external (offline)</span>
                </span>
              ) : "Unassigned"
            }
          />
          {r.engine_target_kind === "group" && r.engine_group && (
            <Row label="Engine group" value={r.engine_group} />
          )}
          {r.overdue && (
            <div className="col-span-2 mt-1 rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-[12px] text-red-800">
              This periodic task is currently <strong>overdue</strong> — a job will be created on the next scheduler run, or trigger it now.
            </div>
          )}
          {r.description && (
            <div className="col-span-2 mt-2">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Description</div>
              <div className="text-sm text-zinc-700 whitespace-pre-wrap">{r.description}</div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Close</Button>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            {isDeleted && (
              <span className="text-xs text-zinc-500 italic self-center" data-testid="routine-detail-deleted-badge">
                Deleted on {new Date(r.deleted_at).toLocaleDateString()}
                {r.deleted_by_name ? ` by ${r.deleted_by_name}` : ""}
              </span>
            )}
            {canDeleteThis && (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                onClick={onDeleteRequest}
                data-testid="routine-detail-delete"
              >
                <Trash2 className="h-4 w-4 mr-2" />Delete
              </Button>
            )}
            {canEditThis && (
              <Button
                variant="outline"
                onClick={onEdit}
                className="w-full sm:w-auto"
                data-testid="routine-detail-edit"
              >
                <Pencil className="h-4 w-4 mr-2" />Edit
              </Button>
            )}
            {!isDeleted && can("routine:spawn") && (
              <Button
                onClick={trigger}
                disabled={triggering}
                className="w-full sm:w-auto bg-ink hover:bg-black"
                data-testid="routine-detail-trigger"
              >
                {triggering ? "Triggering…" : "Trigger now"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function _yachtUnitsForSystem(yacht, system) {
  /**
   * Returns the list of selectable units on the yacht for the given
   * System category. Each entry: {id, label}. Empty list when the system
   * isn't engine-like or no units of that kind exist on the yacht.
   *
   * 2026-06-11 rewrite: labels are now driven by the physical location
   * (Port / Center / Starboard / …) — the same Name column shown in the
   * Yacht editor — instead of brand + type + ordinal. Same fallback
   * chain as the Logbook so a unit with no explicit ``location`` still
   * gets a sensible positional default ("Port", "Center", "Starboard")
   * instead of the legacy "Engine #1" labels which are impossible to
   * map back to a specific hull position.
   */
  if (!yacht || !system) return [];
  const out = [];
  const collect = (items, kind) => {
    const list = (items || []).filter((u) => u && u.id);
    list.forEach((unit, idx) => {
      const loc = resolveUnitLocation(unit, idx, list.length) || kind;
      const label = unit.group ? `[${unit.group}] ${loc}` : loc;
      out.push({ id: unit.id, label, hours: unit.hours ?? null, group: unit.group || null });
    });
  };
  if (system === "Engines & Propulsion") {
    collect(yacht.engines, "Engine");
  } else if (system === "Generators") {
    collect(yacht.generators, "Generator");
  } else if (system === "Stabilizers") {
    collect(yacht.stabilizers, "Stabilizer");
  } else if (system === "Tenders & Toys") {
    (yacht.tenders || []).forEach((t) => {
      const tEngines = (t.engines || []).filter((e) => e && e.id);
      tEngines.forEach((e, idx) => {
        const loc = resolveUnitLocation(e, idx, tEngines.length);
        const label = loc ? `${t.name || "Tender"} · ${loc}` : (t.name || "Tender");
        out.push({ id: e.id, label, hours: e.hours ?? null, group: e.group || null });
      });
    });
  }
  return out;
}

function _yachtEngineOptions(yacht) {
  // Flat list of every selectable engine-like unit on the yacht — main
  // engines, generators, tender engines. Same location-driven labels as
  // ``_yachtUnitsForSystem`` so a single-task ad-hoc job-form keeps the
  // same naming as the periodic-task editor and the Logbook.
  const out = [];
  const collect = (items, kindLabel) => {
    const list = (items || []).filter((u) => u && u.id);
    list.forEach((unit, idx) => {
      const loc = resolveUnitLocation(unit, idx, list.length) || kindLabel;
      const label = unit.group
        ? `[${unit.group}] ${kindLabel} · ${loc}`
        : `${kindLabel} · ${loc}`;
      out.push({ id: unit.id, label });
    });
  };
  collect(yacht?.engines, "Engine");
  collect(yacht?.generators, "Generator");
  collect(yacht?.stabilizers, "Stabilizer");
  (yacht?.tenders || []).forEach((t) => {
    const tEngines = (t.engines || []).filter((e) => e && e.id);
    tEngines.forEach((e, idx) => {
      const loc = resolveUnitLocation(e, idx, tEngines.length);
      const label = loc
        ? `${t.name || "Tender"} · ${loc}`
        : `${t.name || "Tender"} engine`;
      out.push({ id: e.id, label });
    });
  });
  return out;
}

function _yachtEngineGroups(yacht) {
  const seen = new Set();
  (yacht?.engines || []).forEach((e) => {
    if (e.group && e.group.trim()) seen.add(e.group.trim());
  });
  (yacht?.generators || []).forEach((g) => {
    if (g.group && g.group.trim()) seen.add(g.group.trim());
  });
  (yacht?.stabilizers || []).forEach((s) => {
    if (s.group && s.group.trim()) seen.add(s.group.trim());
  });
  (yacht?.tenders || []).forEach((t) => {
    (t.engines || []).forEach((e) => {
      if (e.group && e.group.trim()) seen.add(e.group.trim());
    });
  });
  return Array.from(seen);
}

function _yachtGroupsForSystem(yacht, system) {
  /** Per-system groups list. Mirrors _yachtUnitsForSystem so the
      "Group" picker reflects only groups that exist within the picked
      system bucket (avoids showing engine groups when Generator is the
      active system). */
  if (!yacht || !system) return [];
  const seen = new Set();
  if (system === "Engines & Propulsion") {
    (yacht.engines || []).forEach((e) => { if (e.group?.trim()) seen.add(e.group.trim()); });
  } else if (system === "Generators") {
    (yacht.generators || []).forEach((g) => { if (g.group?.trim()) seen.add(g.group.trim()); });
  } else if (system === "Stabilizers") {
    (yacht.stabilizers || []).forEach((s) => { if (s.group?.trim()) seen.add(s.group.trim()); });
  } else if (system === "Tenders & Toys") {
    (yacht.tenders || []).forEach((t) => {
      (t.engines || []).forEach((e) => { if (e.group?.trim()) seen.add(e.group.trim()); });
    });
  }
  return Array.from(seen);
}

function RoutineEditor({ template, yacht_id, members, inventory, suppliers, used = [], onClose, onSaved }) {
  const { activeYacht } = useYacht();
  const { activeTour } = useTour();
  // Fetch the live yacht doc directly from /yachts/{yacht_id} so the engine
  // & group dropdowns never depend on a potentially-stale YachtContext.
  // Falls back to the context value if the request hasn't resolved yet.
  const [liveYacht, setLiveYacht] = useState(
    activeYacht && activeYacht.id === yacht_id ? activeYacht : null,
  );
  useEffect(() => {
    if (!yacht_id) return;
    let alive = true;
    api.get(`/yachts/${yacht_id}`)
      .then((r) => { if (alive) setLiveYacht(r.data); })
      .catch(() => { /* keep the context fallback */ });
    return () => { alive = false; };
  }, [yacht_id]);
  const yachtForSpec = liveYacht || (activeYacht && activeYacht.id === yacht_id ? activeYacht : null);

  const [form, setForm] = useState(template ? {
    ...ROUTINE_DEFAULT,
    ...template,
    parts: template.parts_template || [],
    next_due_hours: template.next_due_hours == null ? "" : String(template.next_due_hours),
    due_days: template.due_days == null ? "" : String(template.due_days),
    // "Whichever-first" secondary trigger — hydrate from secondary_value.
    has_secondary: template.secondary_value != null,
    secondary_value: template.secondary_value == null ? "" : String(template.secondary_value),
    engine_target_kind: template.engine_target_kind || "engine",
    engine_target_id: template.engine_target_id || "",
    engine_group: template.engine_group || "",
    // Hydrate the external-supplier state from the persisted template so an
    // Edit dialog opens already in the right mode. An online supplier
    // (provider crew) lives on assigned_to → also open in external mode.
    assignee_type: ((template.quote_suppliers || []).length
      || template.external_supplier?.supplier_name
      || (template.assigned_to && (members || []).some((m) => m.role === "provider" && (m.user_id || m.id) === template.assigned_to)))
      ? "external" : "crew",
    quote_suppliers: template.quote_suppliers || [],
    external_supplier_id: template.external_supplier?.supplier_id || "",
    external_supplier_name: template.external_supplier?.supplier_name || "",
    // Calendar-mode next-due override. Pre-fill from the stored next_due so an
    // Edit dialog shows the current scheduled date and lets the user move it.
    // next_due is set whenever a days component exists (days-primary OR an
    // hours-primary 'whichever-first' task with a secondary days interval).
    first_due_date: template.next_due ? template.next_due.slice(0, 10) : (ROUTINE_DEFAULT.first_due_date || ""),
  } : ROUTINE_DEFAULT);
  const isEdit = !!template;
  // Legacy direct-assign supplier templates (external_supplier / provider
  // assignee, no quote_suppliers) keep the old picker on edit; everything
  // else uses the "My suppliers" quote flow.
  const legacyExternal = isEdit
    && !((template.quote_suppliers || []).length)
    && !!(template.external_supplier?.supplier_name
      || (template.assigned_to && (members || []).some((m) => m.role === "provider" && (m.user_id || m.id) === template.assigned_to)));
  // ``saving`` guards the Save button against duplicate POSTs from
  // impatient repeat-clicks (same fix as the one-off editor above).
  const [saving, setSaving] = useState(false);
  // Per-system unit + group lists. The picker visibility is tied to
  // ``system_category`` (engine-like systems only), independent of the
  // cycle basis — see ENGINE_LIKE_SYSTEMS for the full list.
  const unitOptions = _yachtUnitsForSystem(yachtForSpec, form.system_category);
  const groupOptions = _yachtGroupsForSystem(yachtForSpec, form.system_category);
  const showUnitPicker = ENGINE_LIKE_SYSTEMS.has(form.system_category);
  // Current hours of the picked target — the latest reading the logbook has
  // recorded on that unit (or the leading unit of a group). Surfaced under
  // the unit picker so the user can set a sensible "target hours" value.
  const selectedUnitHours = (() => {
    if (!showUnitPicker) return null;
    if (form.engine_target_kind === "engine") {
      const u = unitOptions.find((o) => o.id === form.engine_target_id);
      return u && u.hours != null ? u.hours : null;
    }
    if (form.engine_target_kind === "group" && form.engine_group) {
      const inGroup = unitOptions.filter((o) => o.group === form.engine_group && o.hours != null);
      if (!inGroup.length) return null;
      return Math.max(...inGroup.map((o) => o.hours));  // leading unit drives the schedule
    }
    return null;
  })();
  // Which cycle components are active. A 'whichever-first' task has both.
  const hasDaysComponent = form.recurrence_kind === "days" || (form.has_secondary && form.recurrence_kind === "engine_hours");
  const hasHoursComponent = form.recurrence_kind === "engine_hours" || (form.has_secondary && form.recurrence_kind === "days");
  // Effective date of the first auto-generated job: the pinned first-due date,
  // else today + N days (matches the server default when first_due_date blank).
  const firstJobDateLabel = (() => {
    const n = Number(form.recurrence_value) || 0;
    const d = form.first_due_date
      ? new Date(`${form.first_due_date}T00:00:00`)
      : new Date(Date.now() + n * 86400000);
    return Number.isNaN(d.getTime())
      ? "—"
      : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  })();
  const unitLabelByKind = form.system_category === "Generators"
    ? "Generator"
    : form.system_category === "Stabilizers"
      ? "Stabilizer"
      : form.system_category === "Tenders & Toys"
        ? "Tender engine"
        : "Engine";

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // System category MUST be picked (the editor now starts blank to
      // force the user to make an explicit choice — see ROUTINE_DEFAULT).
      if (!form.system_category) {
        throw new Error("Pick a Category before saving.");
      }
      // When the system is engine-like, the user must also pin the
      // routine to a specific unit (or a group). This is independent of
      // the cycle basis.
      if (showUnitPicker) {
        if (form.engine_target_kind === "engine" && !form.engine_target_id) {
          throw new Error(`Pick which ${unitLabelByKind.toLowerCase()} this routine tracks.`);
        }
        if (form.engine_target_kind === "group" && !form.engine_group) {
          throw new Error("Pick which group this routine tracks.");
        }
      }
      // Any hours component (primary engine-hours, or a secondary hours
      // interval on a days-primary task) needs a target hour value to anchor
      // against. The engine/group pin is validated above by the unit-picker.
      if (hasHoursComponent) {
        if (!form.next_due_hours || Number.isNaN(Number(form.next_due_hours))) {
          throw new Error("Pick the target hours for the next service.");
        }
        if (!showUnitPicker) {
          throw new Error("Engine-hours tracking needs an engine-like system with an engine pinned.");
        }
      }
      // 'Whichever-first' second interval must be a positive number.
      if (form.has_secondary && (!form.secondary_value || Number(form.secondary_value) < 1)) {
        throw new Error("Enter the second interval (or turn off the second trigger).");
      }
      // External-supplier resolution — mirrors the one-off editor. Online
      // supplier (provider crew) is recorded via assigned_to; offline via
      // external_supplier {id, name}. New offline rows persist to /suppliers.
      let external_supplier = null;
      let assignee_id = form.assigned_to || null;
      if (form.assignee_type === "external" && !legacyExternal) {
        // "My suppliers" quote flow — spawned jobs auto-invite the selected
        // network suppliers through the Supplier Hub.
        if (!(form.quote_suppliers || []).length) {
          throw new Error("Select at least one supplier to request quotes from.");
        }
        external_supplier = null;
        assignee_id = null;
      } else if (form.assignee_type === "external") {
        if (form.assigned_to) {
          external_supplier = null;
          assignee_id = form.assigned_to;
        } else {
          if (!(form.external_supplier_name || "").trim()) {
            throw new Error("Pick a supplier or type a name.");
          }
          if (form._new_supplier && form._new_supplier_save === null) {
            throw new Error("Choose whether to save this supplier to your network.");
          }
          if (form._new_supplier && form._new_supplier_save === true) {
            if (!(form._new_supplier_email || "").trim() || !(form._new_supplier_phone || "").trim()) {
              throw new Error("Email and phone are required to save a supplier to your network.");
            }
          }
          let id = form.external_supplier_id || null;
          let name = (form.external_supplier_name || "").trim();
          if (form._new_supplier && form._new_supplier_save === true) {
            const { data: sup } = await api.post("/suppliers", {
              type: form._new_supplier_type || "company",
              name,
              contact_person: form._new_supplier_contact_person || "",
              email: (form._new_supplier_email || "").trim(),
              phone: (form._new_supplier_phone || "").trim(),
              category: form._new_supplier_category || "other",
              notes: "",
            });
            try { await api.post(`/suppliers/${sup.id}/assign`, { yacht_id }); } catch { /* non-fatal */ }
            id = sup.id; name = sup.name;
          }
          external_supplier = { supplier_id: id, supplier_name: name };
          assignee_id = null;
        }
      }
      const payload = {
        ...form,
        yacht_id,
        assigned_to: form.assignee_type === "external" ? assignee_id : (form.assigned_to || null),
        external_supplier,
        quote_suppliers: form.assignee_type === "external" && !legacyExternal ? (form.quote_suppliers || []) : [],
        recurrence_value: Number(form.recurrence_value),
        // Per-task due window for generated jobs. Blank → null → server falls
        // back to the cycle interval (days) or 14 (engine hours).
        due_days: form.due_days !== "" && form.due_days != null ? Number(form.due_days) : null,
        // 'Whichever-first' second interval (opposite unit), or null when off.
        secondary_value: form.has_secondary ? Number(form.secondary_value) : null,
        // Hours target — set whenever an hours component exists, else cleared.
        next_due_hours: hasHoursComponent ? Number(form.next_due_hours) : null,
        // Calendar-mode next-due date. Set whenever a days component exists.
        // When the user sets/edits the date we store it verbatim (ISO), both at
        // create AND edit time, so the scheduler tracks the cycle from there.
        // Blank → keep the existing next_due (edit) or let the server default
        // to now + N days (create). Cleared to null when no days component.
        next_due: hasDaysComponent
          ? (form.first_due_date
            ? new Date(`${form.first_due_date}T00:00:00Z`).toISOString()
            : (form.next_due || null))
          : null,
        // Unit pin is tied to System (engine-like systems only), NOT to
        // cycle basis — so a days-cycle routine on "Engines" still pins
        // itself to a specific engine for reporting/grouping.
        engine_target_kind: showUnitPicker ? form.engine_target_kind : null,
        engine_target_id: showUnitPicker && form.engine_target_kind === "engine" ? form.engine_target_id : null,
        engine_group: showUnitPicker && form.engine_target_kind === "group" ? form.engine_group : null,
        parts_template: form.parts.map((p) => ({
          item_id: p.item_id, item_name: p.item_name, qty: Number(p.qty),
        })),
      };
      delete payload.parts;
      // Strip UI-only fields before send.
      delete payload.first_due_date;
      delete payload.assignee_type;
      delete payload.external_supplier_id;
      delete payload.external_supplier_name;
      delete payload._new_supplier;
      delete payload._new_supplier_save;
      delete payload._new_supplier_type;
      delete payload._new_supplier_category;
      delete payload._new_supplier_contact_person;
      delete payload._new_supplier_email;
      delete payload._new_supplier_phone;
      if (isEdit) await api.patch(`/routine-maintenance/${template.id}`, payload);
      else await api.post(`/routine-maintenance`, payload);
      toast.success(`Routine ${isEdit ? "saved" : "created"}`);
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        data-testid="routine-editor"
        onPointerDownOutside={(e) => { if (activeTour) e.preventDefault(); }}
        onInteractOutside={(e) => { if (activeTour) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (activeTour) e.preventDefault(); }}
        onOpenAutoFocus={(e) => { if (activeTour) e.preventDefault(); }}
      >
        <DialogHeader><DialogTitle>{isEdit ? "Edit periodic task" : "New periodic task"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Title *" className="md:col-span-2">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="routine-title" />
          </Field>
          <Field label="Category *">
            <SystemSelect
              value={form.system_category}
              onChange={(v) => setForm({
                ...form,
                system_category: v,
                // Reset the unit pin when switching System — engines from
                // a previous selection won't be valid in the new bucket.
                engine_target_id: "",
                engine_group: "",
              })}
              used={used}
              placeholder="— Select category —"
              testid="routine-system"
            />
          </Field>
          <Field label="Priority">
            <select className="h-9 w-full rounded-md border border-input px-3 text-sm" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {PRIORITY.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Cycle basis">
            <select className="h-9 w-full rounded-md border border-input px-3 text-sm" value={form.recurrence_kind} onChange={(e) => setForm({ ...form, recurrence_kind: e.target.value })} data-testid="routine-cycle-kind">
              <option value="days">Calendar days</option>
              <option value="engine_hours">Engine hours</option>
            </select>
          </Field>
          <Field label={form.recurrence_kind === "engine_hours" ? "Every N hours" : "Every N days"}>
            <Input type="number" min="1" value={form.recurrence_value} onChange={(e) => setForm({ ...form, recurrence_value: e.target.value })} data-testid="routine-cycle" />
          </Field>
          <Field label="Job due window (days)">
            <Input
              type="number"
              min="0"
              value={form.due_days}
              onChange={(e) => setForm({ ...form, due_days: e.target.value })}
              placeholder={form.recurrence_kind === "days" ? `defaults to ${form.recurrence_value || "N"}` : "defaults to 14"}
              data-testid="routine-due-days"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              When a job is generated, it becomes due this many days later. Leave blank to use the {form.recurrence_kind === "days" ? "cycle interval" : "14-day default"}.
            </div>
          </Field>

          {/* "Whichever comes first" — add a second trigger on the opposite
              unit. Hours-as-secondary needs an engine, so only offer that case
              on engine-like systems. */}
          {(form.recurrence_kind === "engine_hours" || showUnitPicker) && (
            <Field label="Second trigger" className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.has_secondary}
                  onChange={(e) => setForm({ ...form, has_secondary: e.target.checked })}
                  data-testid="routine-secondary-toggle"
                />
                Also expire on {form.recurrence_kind === "days" ? "engine hours" : "calendar days"} — whichever comes first
              </label>
            </Field>
          )}
          {form.has_secondary && (
            <Field label={form.recurrence_kind === "days" ? "Also every N engine hours" : "Also every N days"}>
              <Input
                type="number"
                min="1"
                value={form.secondary_value}
                onChange={(e) => setForm({ ...form, secondary_value: e.target.value })}
                data-testid="routine-secondary-value"
              />
            </Field>
          )}

          {hasDaysComponent && (
            <Field label={isEdit ? "Next due date" : "First due date (optional)"}>
              <Input
                type="date"
                value={form.first_due_date}
                onChange={(e) => setForm({ ...form, first_due_date: e.target.value })}
                placeholder="defaults to today + N days"
                data-testid="routine-first-due"
              />
              <div className="text-[11px] text-zinc-500 mt-1">
                {form.first_due_date
                  ? <>First job will be generated on <span className="font-medium text-zinc-700">{firstJobDateLabel}</span> (and every {form.recurrence_value} day{Number(form.recurrence_value) === 1 ? "" : "s"} after).</>
                  : <>Leave blank to schedule the first job <span className="font-medium text-zinc-700">{form.recurrence_value} day{Number(form.recurrence_value) === 1 ? "" : "s"} from today</span> ({firstJobDateLabel}). Pick today to generate it now.</>}
              </div>
            </Field>
          )}
          {hasHoursComponent && (
            <Field label="Target hours for next service *">
              <Input
                type="number"
                min="0"
                value={form.next_due_hours}
                onChange={(e) => setForm({ ...form, next_due_hours: e.target.value })}
                placeholder="e.g. 500"
                data-testid="routine-next-hours"
              />
            </Field>
          )}
          {showUnitPicker && (
            <>
              <Field label="Track">
                <select
                  className="h-9 w-full rounded-md border border-input px-3 text-sm"
                  value={form.engine_target_kind}
                  onChange={(e) => setForm({ ...form, engine_target_kind: e.target.value })}
                  data-testid="routine-target-kind"
                >
                  <option value="engine">Specific {unitLabelByKind.toLowerCase()}</option>
                  <option value="group" disabled={groupOptions.length === 0}>
                    Group{groupOptions.length === 0 ? " (none defined)" : ""}
                  </option>
                </select>
              </Field>
              {form.engine_target_kind === "engine" ? (
                <Field label={`${unitLabelByKind} *`} className="md:col-span-2">
                  <select
                    className="h-9 w-full rounded-md border border-input px-3 text-sm"
                    value={form.engine_target_id}
                    onChange={(e) => setForm({ ...form, engine_target_id: e.target.value })}
                    data-testid="routine-engine"
                  >
                    <option value="">Pick a {unitLabelByKind.toLowerCase()}…</option>
                    {unitOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                  {unitOptions.length === 0 && (
                    <div className="mt-1 text-[11px] text-amber-700">
                      No {unitLabelByKind.toLowerCase()}s defined on this yacht yet. Add them under Yachts → Edit first.
                    </div>
                  )}
                  {form.engine_target_id && (
                    <div className="mt-1 text-[11px] text-zinc-600" data-testid="routine-current-hours">
                      {selectedUnitHours != null
                        ? <>Current hours: <span className="font-medium text-zinc-800">{Math.round(selectedUnitHours)} h</span></>
                        : <span className="text-zinc-400">No hours logged yet for this {unitLabelByKind.toLowerCase()}.</span>}
                    </div>
                  )}
                </Field>
              ) : (
                <Field label="Group *" className="md:col-span-2">
                  <select
                    className="h-9 w-full rounded-md border border-input px-3 text-sm"
                    value={form.engine_group}
                    onChange={(e) => setForm({ ...form, engine_group: e.target.value })}
                    data-testid="routine-group"
                  >
                    <option value="">Pick a group…</option>
                    {groupOptions.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  {form.recurrence_kind === "engine_hours" && (
                    <div className="mt-1 text-[11px] text-zinc-500">
                      Fires when the leading unit in the group reaches the target hours.
                    </div>
                  )}
                  {form.engine_group && (
                    <div className="mt-1 text-[11px] text-zinc-600" data-testid="routine-current-hours-group">
                      {selectedUnitHours != null
                        ? <>Leading unit current hours: <span className="font-medium text-zinc-800">{Math.round(selectedUnitHours)} h</span></>
                        : <span className="text-zinc-400">No hours logged yet for this group.</span>}
                    </div>
                  )}
                </Field>
              )}
            </>
          )}
          <Field label="Default assignee" className="md:col-span-2">
            <div className="space-y-2">
              <div className="flex gap-1 rounded-md border border-zinc-200 bg-zinc-50 p-1 w-fit text-xs" data-testid="routine-assignee-toggle">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, assignee_type: "crew", assigned_to: members.some((m) => m.role === "provider" && (m.user_id || m.id) === form.assigned_to) ? "" : form.assigned_to, external_supplier_id: "", external_supplier_name: "", _new_supplier: false, _new_supplier_save: null })}
                  className={`px-3 py-1 rounded ${form.assignee_type === "crew" ? "bg-white shadow-sm font-medium" : "text-zinc-600"}`}
                  data-testid="routine-assignee-crew"
                >Crew member</button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, assignee_type: "external", assigned_to: legacyExternal ? form.assigned_to : "" })}
                  className={`px-3 py-1 rounded ${form.assignee_type === "external" ? "bg-white shadow-sm font-medium" : "text-zinc-600"}`}
                  data-testid="routine-assignee-external"
                >My suppliers</button>
              </div>
              {form.assignee_type === "crew" ? (
                <select
                  className="h-9 w-full rounded-md border border-input px-3 text-sm"
                  value={form.assigned_to || ""}
                  onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                  data-testid="routine-assigned-to"
                >
                  <option value="">Unassigned</option>
                  {members.filter((m) => m.role !== "provider").map((m) => (
                    <option key={m.user_id || m.id} value={m.user_id || m.id}>
                      {(m.name || m.email || "—")} · {m.role}
                    </option>
                  ))}
                </select>
              ) : legacyExternal ? (
                <ExternalSupplierPicker form={form} setForm={setForm} suppliers={suppliers} providerMembers={members.filter((m) => m.role === "provider")} />
              ) : (
                <FindSupplierPicker
                  yachtId={yacht_id}
                  defaultCategory={form.system_category}
                  selected={form.quote_suppliers}
                  onChange={(sel) => setForm({ ...form, quote_suppliers: sel })}
                  networkOnly
                />
              )}
              {form.assignee_type === "external" && legacyExternal && (
                <div className="text-[11px] text-zinc-500">
                  Every spawned job will be pinned to this supplier. You move each job through
                  statuses on their behalf, and an invoice will be auto-created on confirmed (when you attach one).
                </div>
              )}
              {form.assignee_type === "external" && !legacyExternal && (
                <div className="text-[11px] text-zinc-500">
                  Every spawned job automatically sends a quote request to the selected supplier(s)
                  through the Supplier Hub — same flow as Find supplier.
                </div>
              )}
            </div>
          </Field>
          <div className="md:col-span-2">
            <PartsPicker
              parts={form.parts}
              inventory={inventory}
              onChange={(parts) => setForm({ ...form, parts })}
            />
          </div>
          <Field label="Description" className="md:col-span-2">
            <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            data-testid="cancel-routine"
          >Cancel</Button>
          <Button
            onClick={save}
            className="bg-ink hover:bg-black"
            disabled={!form.title || !form.system_category || !form.recurrence_value || saving}
            data-testid="save-routine"
          >
            {saving ? "Saving…" : (isEdit ? "Save" : "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
